import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Clock, Download, AlertCircle } from 'lucide-react';
import { UserSubscriptionInfo } from '../../types/subscription';
import { formatDate } from '../../utils/formatters';

// Mock data - in a real app, this would come from an API
const mockSubscription: UserSubscriptionInfo = {
  currentPlan: {
    id: 'pro',
    name: 'Professional',
    tier: 'pro',
    price: 19.99,
    interval: 'monthly',
    features: [],
    limits: {
      posts: 100,
      articles: 20,
      videos: 10,
      storage: 10,
      analytics: true,
      api: true,
      support: 'email'
    }
  },
  status: 'active',
  usage: {
    posts: 45,
    articles: 8,
    videos: 3,
    storage: 4.2
  },
  billingHistory: [
    {
      id: '1',
      date: new Date('2024-03-01'),
      amount: 19.99,
      description: 'Professional Plan - Monthly',
      status: 'paid'
    },
    {
      id: '2',
      date: new Date('2024-02-01'),
      amount: 19.99,
      description: 'Professional Plan - Monthly',
      status: 'paid'
    }
  ]
};

export default function SubscriptionSettings() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const getUsagePercentage = (used: number, limit: number) => {
    return Math.min((used / limit) * 100, 100);
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className={`
        p-6 rounded-lg border
        ${theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
        }
      `}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-xl font-bold mb-1 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {mockSubscription.currentPlan.name}
            </h3>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
              ${mockSubscription.currentPlan.price}/month
            </p>
          </div>
          <button
            onClick={() => navigate('/pricing')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upgrade Plan
          </button>
        </div>

        {/* Usage Stats */}
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-1">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Posts ({mockSubscription.usage.posts}/{mockSubscription.currentPlan.limits.posts})
              </span>
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {getUsagePercentage(
                  mockSubscription.usage.posts,
                  mockSubscription.currentPlan.limits.posts
                ).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700">
              <div
                className="h-2 bg-blue-600 rounded-full"
                style={{
                  width: `${getUsagePercentage(
                    mockSubscription.usage.posts,
                    mockSubscription.currentPlan.limits.posts
                  )}%`
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-1">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Articles ({mockSubscription.usage.articles}/{mockSubscription.currentPlan.limits.articles})
              </span>
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {getUsagePercentage(
                  mockSubscription.usage.articles,
                  mockSubscription.currentPlan.limits.articles
                ).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700">
              <div
                className="h-2 bg-green-600 rounded-full"
                style={{
                  width: `${getUsagePercentage(
                    mockSubscription.usage.articles,
                    mockSubscription.currentPlan.limits.articles
                  )}%`
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-1">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Videos ({mockSubscription.usage.videos}/{mockSubscription.currentPlan.limits.videos})
              </span>
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {getUsagePercentage(
                  mockSubscription.usage.videos,
                  mockSubscription.currentPlan.limits.videos
                ).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700">
              <div
                className="h-2 bg-purple-600 rounded-full"
                style={{
                  width: `${getUsagePercentage(
                    mockSubscription.usage.videos,
                    mockSubscription.currentPlan.limits.videos
                  )}%`
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-1">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Storage ({mockSubscription.usage.storage.toFixed(1)}/{mockSubscription.currentPlan.limits.storage}GB)
              </span>
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {getUsagePercentage(
                  mockSubscription.usage.storage,
                  mockSubscription.currentPlan.limits.storage
                ).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700">
              <div
                className="h-2 bg-yellow-600 rounded-full"
                style={{
                  width: `${getUsagePercentage(
                    mockSubscription.usage.storage,
                    mockSubscription.currentPlan.limits.storage
                  )}%`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className={`
        p-6 rounded-lg border
        ${theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
        }
      `}>
        <h3 className={`text-lg font-semibold mb-4 ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Payment Method
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CreditCard className="h-6 w-6 text-blue-500" />
            <div>
              <p className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
                •••• •••• •••• 4242
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Expires 12/25
              </p>
            </div>
          </div>
          <button
            className={`
              px-4 py-2 rounded-lg transition-colors
              ${theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            Update
          </button>
        </div>
      </div>

      {/* Billing History */}
      <div className={`
        p-6 rounded-lg border
        ${theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
        }
      `}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Billing History
          </h3>
          <button
            className={`
              flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors
              ${theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            <Download className="h-4 w-4" />
            <span>Download All</span>
          </button>
        </div>

        <div className="space-y-4">
          {mockSubscription.billingHistory.map(bill => (
            <div
              key={bill.id}
              className={`
                flex items-center justify-between p-4 rounded-lg
                ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}
              `}
            >
              <div className="flex items-center space-x-4">
                <Clock className="h-5 w-5 text-blue-500" />
                <div>
                  <p className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
                    {bill.description}
                  </p>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {formatDate(bill.date)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`font-medium ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  ${bill.amount.toFixed(2)}
                </span>
                <span className={`
                  px-2 py-1 text-xs rounded-full
                  ${bill.status === 'paid'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }
                `}>
                  {bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel Subscription */}
      <div className={`
        p-6 rounded-lg border border-red-200 dark:border-red-900
        ${theme === 'dark' ? 'bg-red-900/10' : 'bg-red-50'}
      `}>
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
          <div>
            <h3 className={`text-lg font-semibold mb-2 ${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            }`}>
              Cancel Subscription
            </h3>
            <p className={`text-sm mb-4 ${
              theme === 'dark' ? 'text-red-300' : 'text-red-600'
            }`}>
              Warning: Canceling your subscription will limit your account to the free tier features
              at the end of your current billing period.
            </p>
            <button
              className={`
                px-4 py-2 rounded-lg transition-colors
                ${theme === 'dark'
                  ? 'bg-red-900/50 text-red-300 hover:bg-red-900/70'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                }
              `}
            >
              Cancel Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}